/**
 * State management helper for the Dynamic Professional Designations form
 * Supports dynamic designation rows fetched from API
 */

import { PD_ACTIONS, ROW_STATUS } from "../../../../constants/professionalDesignations";
import Moment from "moment";

/**
 * Initial state for the dynamic professional designations form
 */
export const pdDynamicInitialState = {
  // Radio button state
  hasDesignations: null,
  hasDesignationsError: false,

  // Available designations fetched from API
  availableDesignations: [],
  isLoadingDesignations: false,

  // Dynamic designation rows
  designationRows: [],
  
  // Store original rows when switching to No, so they can be restored when switching back to Yes
  preservedDesignationRows: [],

  // Track deleted rows that need to be removed from server (only existing rows)
  deletedRows: [],

  // Original rows snapshot for comparison (rows loaded from API)
  originalRows: [],

  // Form state
  isDataFetched: false,
  showBackModal: false,
  formHasChanged: false,
};

/**
 * Generate a unique ID for new designation rows
 */
export const generateRowId = () => {
  return `row_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * Create a new empty designation row
 */
export const createEmptyDesignationRow = () => ({
  id: generateRowId(),
  candidateDesignationId: null,
  designationId: null,
  designationName: "",
  issueDate: "",
  status: ROW_STATUS.NEW,
  errors: {
    designation: false,
    designationText: "",
    issueDate: false,
    issueDateText: "",
  },
});

/**
 * Check if a row has been modified compared to its original state
 */
const isRowModified = (row, originalRows) => {
  const original = originalRows.find((orig) => orig.id === row.id);
  if (!original) return false;

  return (
    original.designationId !== row.designationId ||
    original.designationName !== row.designationName ||
    original.issueDate !== row.issueDate
  );
};

/**
 * Reducer for the dynamic professional designations form
 */
export const pdDynamicReducer = (state = pdDynamicInitialState, action = {}) => {
  switch (action.type) {
    case PD_ACTIONS.SET_LOADING_DESIGNATIONS:
      return {
        ...state,
        isLoadingDesignations: action.payload,
      };

    case PD_ACTIONS.SET_AVAILABLE_DESIGNATIONS:
      return {
        ...state,
        availableDesignations: action.payload,
        isLoadingDesignations: false,
      };

    case PD_ACTIONS.SET_HAS_DESIGNATIONS:
      if (action.payload === false) {
        const existingRowsToDelete = state.designationRows.filter(
          (row) => row.status !== ROW_STATUS.NEW
        );
        return {
          ...state,
          hasDesignations: false,
          hasDesignationsError: false,
          preservedDesignationRows: state.designationRows.length > 0 ? state.designationRows : state.preservedDesignationRows,
          designationRows: [],
          deletedRows: [...state.deletedRows, ...existingRowsToDelete],
          formHasChanged: true,
        };
      } else {
        const restoredRows = state.designationRows.length > 0 ? state.designationRows : state.preservedDesignationRows;
        const restoredDesignationIds = restoredRows.map(r => r.designationId).filter(Boolean);
        return {
          ...state,
          hasDesignations: true,
          hasDesignationsError: false,
          designationRows: restoredRows,
          deletedRows: state.deletedRows.filter(
            (row) => !restoredDesignationIds.includes(row.designationId)
          ),
          formHasChanged: true,
        };
      }

    case PD_ACTIONS.SET_HAS_DESIGNATIONS_ERROR:
      return {
        ...state,
        hasDesignationsError: action.payload,
      };

    case PD_ACTIONS.ADD_DESIGNATION_ROW:
      return {
        ...state,
        designationRows: [...state.designationRows, createEmptyDesignationRow()],
        formHasChanged: true,
      };

    case PD_ACTIONS.REMOVE_DESIGNATION_ROW: {
      const rowToRemove = state.designationRows.find(
        (row) => row.id === action.payload
      );
      const newDeletedRows =
        rowToRemove && rowToRemove.status !== ROW_STATUS.NEW
          ? [...state.deletedRows, rowToRemove]
          : state.deletedRows;

      return {
        ...state,
        designationRows: state.designationRows.filter(
          (row) => row.id !== action.payload
        ),
        deletedRows: newDeletedRows,
        formHasChanged: true,
      };
    }

    case PD_ACTIONS.UPDATE_DESIGNATION_ROW:
      return {
        ...state,
        designationRows: state.designationRows.map((row) => {
          if (row.id !== action.payload.rowId) return row;

          const updatedRow = { ...row, ...action.payload.updates };

          if (row.status !== ROW_STATUS.NEW) {
            const modified = isRowModified(updatedRow, state.originalRows);
            updatedRow.status = modified ? ROW_STATUS.MODIFIED : ROW_STATUS.UNCHANGED;
          }

          return updatedRow;
        }),
        formHasChanged: true,
      };

    case PD_ACTIONS.SET_ROW_ERROR:
      return {
        ...state,
        designationRows: state.designationRows.map((row) =>
          row.id === action.payload.rowId
            ? {
                ...row,
                errors: {
                  ...row.errors,
                  [action.payload.field]: action.payload.hasError,
                  [`${action.payload.field}Text`]: action.payload.errorText,
                },
              }
            : row
        ),
      };

    case PD_ACTIONS.CLEAR_ROW_ERROR:
      return {
        ...state,
        designationRows: state.designationRows.map((row) =>
          row.id === action.payload.rowId
            ? {
                ...row,
                errors: {
                  ...row.errors,
                  [action.payload.field]: false,
                  [`${action.payload.field}Text`]: "",
                },
              }
            : row
        ),
      };

    case PD_ACTIONS.LOAD_EXISTING_DATA: {
      const loadedRows = action.payload.designationRows.map((row) => ({
        ...row,
        status: ROW_STATUS.UNCHANGED,
        originalDesignationId: row.designationId,
        originalCandidateDesignationId: row.candidateDesignationId,
      }));
      return {
        ...state,
        hasDesignations: action.payload.hasDesignations,
        designationRows: loadedRows,
        preservedDesignationRows: loadedRows,
        originalRows: loadedRows.map((row) => ({ ...row })),
        deletedRows: [],
        isDataFetched: true,
        formHasChanged: false,
      };
    }

    case PD_ACTIONS.SET_DATA_FETCHED:
      return {
        ...state,
        isDataFetched: action.payload,
      };

    case PD_ACTIONS.SET_SHOW_BACK_MODAL:
      return {
        ...state,
        showBackModal: action.payload,
      };

    case PD_ACTIONS.SET_FORM_CHANGED:
      return {
        ...state,
        formHasChanged: action.payload,
      };

    case PD_ACTIONS.RESET_CHANGE_TRACKING:
      return {
        ...state,
        designationRows: state.designationRows.map((row) => ({
          ...row,
          status: ROW_STATUS.UNCHANGED,
          originalDesignationId: row.designationId,
        })),
        originalRows: state.designationRows.map((row) => ({ ...row })),
        deletedRows: [],
        formHasChanged: false,
      };

    case PD_ACTIONS.RESET_FORM:
      return {
        ...pdDynamicInitialState,
        availableDesignations: state.availableDesignations,
      };

    default:
      return state;
  }
};

/**
 * Validate all designation rows
 */
export const validateDesignationRows = (designationRows) => {
  let isValid = true;
  const rowErrors = [];

  designationRows.forEach((row) => {
    const errors = {
      rowId: row.id,
      designation: false,
      designationText: "",
      issueDate: false,
      issueDateText: "",
    };

    if (!row.designationId || !row.designationName) {
      errors.designation = true;
      errors.designationText = "Please select a professional designation";
      isValid = false;
    }

    if (!row.issueDate) {
      errors.issueDate = true;
      errors.issueDateText = "Please enter a valid date";
      isValid = false;
    }

    rowErrors.push(errors);
  });

  return { isValid, rowErrors };
};

/**
 * Check if the form can proceed (Next button enabled)
 */
export const canProceed = (state) => {
  if (state.hasDesignations === null) {
    return false;
  }

  if (state.hasDesignations === false) {
    return true;
  }

  if (state.hasDesignations === true) {
    if (state.designationRows.length === 0) {
      return false;
    }

    return state.designationRows.every(
      (row) => {
        return (
          !!row.designationId &&
          !!row.designationName &&
          !!row.issueDate &&
          (row.issueDate ? !row.issueDate.includes("_") : false) &&
          !row.errors.designation &&
          !row.errors.issueDate
        );
      }
    );
  }

  return false;
};

/**
 * Transform API designation data to form row format.
 * @param {Array} apiDesignations - Saved designation records from the server
 * @param {Array} availableDesignations - Full list from getDesignationsByContractType (includes isCria)
 */
export const transformApiDataToRows = (apiDesignations, availableDesignations = []) => {
  if (!Array.isArray(apiDesignations) || apiDesignations.length === 0) {
    return [];
  }

  return apiDesignations.map((designation) => {
    const rawDate = designation.dateacquired;
    const candidateDesignationId = designation.candidatedesignationsid;
    const profDesignationId = designation.profdesignationid;

    const matched = availableDesignations.find((d) => d.id === profDesignationId);

    return {
      id: generateRowId(),
      candidateDesignationId: candidateDesignationId,
      designationId: profDesignationId,
      designationName: designation.designationdescription,
      issueDate: rawDate,
      isCria: matched ? matched.isCria : undefined,
      status: ROW_STATUS.UNCHANGED,
      originalDesignationId: profDesignationId,
      originalCandidateDesignationId: candidateDesignationId,
      errors: {
        designation: false,
        designationText: "",
        issueDate: false,
        issueDateText: "",
      },
    };
  });
};

/**
 * Get rows that need to be saved (new or modified)
 */
export const getRowsToSave = (designationRows) => {
  const newRows = designationRows.filter((row) => row.status === ROW_STATUS.NEW);
  const modifiedRows = designationRows.filter((row) => row.status === ROW_STATUS.MODIFIED);
  return { newRows, modifiedRows };
};


const formatDesignationDescription = (description) => {
  if (!description) return description;
  const match = description.match(/^(.+?)\s*\(([^)]+)\)$/);
  if (match) {
    const fullName = match[1].trim();
    const abbreviation = match[2].trim();
    return `${abbreviation} - ${fullName}`;
  }
  return description;
};

export const syncPdChangesToIaTable = async ({
  profDesCriaData,
  freshProfessionalDesignations = [],
  availableDesignations = [],
  candidateId,
  contractId,
  subId,
  saveprofDesCriaData,
  insertProfessionalDesignationsIA,
  deleteAllPdRecords = false,
}) => {
  if (!profDesCriaData) return null;

  const criaData = Array.isArray(profDesCriaData) && profDesCriaData.length > 0
    ? profDesCriaData[0]
    : (typeof profDesCriaData === 'object' && profDesCriaData !== null ? profDesCriaData : {});

  const iaDesignations = (Array.isArray(criaData.designations)
    ? criaData.designations
    : []
  ).filter((d) => d.source !== "PD");

  const iaOtherDesignations = (Array.isArray(criaData.other_designations)
    ? criaData.other_designations
    : []
  ).filter((d) => d.source !== "PD");

  const pdDesignations = [];
  const pdOtherDesignations = [];

  if (!deleteAllPdRecords) {
    const DATE_FORMATS = ["YYYY-MM-DD", "MM/DD/YYYY", "MM/YYYY", "YYYY-MM"];

    freshProfessionalDesignations.forEach((d) => {
      const matched = availableDesignations.find((a) => a.id === d.profdesignationid);
      const isCria = matched?.isCria === true;
      const rawDate = (d.dateacquired || "").replace(/_/g, "").trim();
      const formattedDate =
          rawDate && Moment(rawDate, DATE_FORMATS, true).isValid()
              ? Moment(rawDate, DATE_FORMATS, true).format("MM/YYYY")
              : rawDate;
      if (isCria) {
        pdDesignations.push({
          state: formatDesignationDescription(d.designationdescription),
          date: formattedDate,
          source: "PD",
          candidatedesignationsid: d.candidatedesignationsid || "",
        });
      } else {
        pdOtherDesignations.push({
          designation: d.designationdescription || "",
          acquiredDate: formattedDate,
          source: "PD",
          candidatedesignationsid: d.candidatedesignationsid || "",
        });
      }
    });
  }

  const updatedCriaData = {
    ...criaData,
    candidateid: candidateId,
    contractid: contractId,
    registrations: criaData.registrations || [],
    designations: [...iaDesignations, ...pdDesignations],
    other_designations: [...iaOtherDesignations, ...pdOtherDesignations],
  };

  if (insertProfessionalDesignationsIA) {
    try {
      await insertProfessionalDesignationsIA(subId, updatedCriaData);
    } catch (error) {
      console.error("Error syncing IA table:", error);
    }
  }

  if (saveprofDesCriaData) {
    saveprofDesCriaData(updatedCriaData);
  }

  return updatedCriaData;
};


